import { Download, X } from "lucide-react"
import React, { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import {
  addCandidateInInterview,
  addCandidateViaFileInInterview,
} from "store/actions/interviewsAction"
import { showAlert } from "store/reducers/alertReducer"
import InterviewCandidatesDemo from "assets/file/interview-candidates-demo.xlsx"
import * as XLSX from "xlsx"

const AddCandidateInPool = ({ toggle, selectedUuid, getAllData }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isLoading } = useSelector(state => state.Interviews)
  const fileInputRef = useRef(null)

  const [national_ids, setNational_ids] = useState([])
  const [inputValue, setInputValue] = useState("")
  const [failed_national_ids, setFailed_national_ids] = useState([])
  const [uploadType, setUploadType] = useState("MANUAL")
  const [file, setFile] = useState(null)
  const [actionType, setActionType] = useState("invited")
  const [notification_message, setNotification_message] = useState("")
  const [saved_count, setSaved_count] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const handleAdd = () => {
    setIsApiCalled(false)
    if (!national_ids) {
      dispatch(showAlert({ text: t("Enter national Id's"), type: "error" }))
      return
    }
    if (actionType === "invited" && !notification_message) {
      dispatch(
        showAlert({ text: t("Enter notification message"), type: "error" })
      )
      return
    }
    const payload = {
      schedule_uuid: selectedUuid, // training uuid
      national_ids: national_ids
        ? national_ids
            ?.split(",")
            ?.map(id => id.trim()) // Remove spaces
            ?.filter(id => id !== "") // Remove empty values
        : [],
      actionType,
      ...(actionType === "invited" && { notification_message }),
    }
    dispatch(addCandidateInInterview(payload))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload || {}
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getAllData()
          setNational_ids([])
          const { failed_national_ids, saved_count } = data || {}
          setFailed_national_ids(failed_national_ids)
          setSaved_count(saved_count)
          if (failed_national_ids?.length === 0) {
            toggle()
          }
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
          setFailed_national_ids([])
          setSaved_count(null)
        }
      })
      .catch(error => {})
      .finally(() => {
        setIsApiCalled(true)
      })
  }

  const handleCsvAdd = () => {
    setIsApiCalled(false)
    if (!file) {
      dispatch(showAlert({ text: t("Upload file"), type: "error" }))
      return
    } else if (actionType === "invited" && !notification_message) {
      dispatch(
        showAlert({ text: t("Enter notification message"), type: "error" })
      )
      return
    }

    let data = new FormData()

    data.append("schedule_uuid", selectedUuid)
    data.append("file", file)
    data.append("actionType", actionType)
    actionType === "invited" &&
      data.append("notification_message", notification_message)

    dispatch(addCandidateViaFileInInterview(data))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload || {}
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          const { failed_national_ids, saved_count } = data || {}
          getAllData()
          setNational_ids([])
          setFailed_national_ids(failed_national_ids)
          setSaved_count(saved_count)
          setFile(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = "" // Reset input field
          }
          if (failed_national_ids?.length === 0) {
            toggle()
          }
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
          setFailed_national_ids([])
          setSaved_count(null)
        }
      })
      .catch(error => {})
      .finally(() => {
        setIsApiCalled(true)
      })
  }

  const handleFileChange = event => {
    const selectedFile = event.target.files[0]
    setFile(selectedFile)
  }

  const downloadFailedIDs = () => {
    if (!failed_national_ids || failed_national_ids.length === 0) {
      dispatch(
        showAlert({ text: t("No failed IDs to download."), type: "error" })
      )
      return
    }

    setIsDownloading(true)

    try {
      // Convert array into a 2D array for Excel
      const data = failed_national_ids.map(id => [id])

      // Create a worksheet and a workbook
      const ws = XLSX.utils.aoa_to_sheet([["Failed National IDs"], ...data]) // Add header
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Failed IDs")

      // Generate and download the Excel file
      XLSX.writeFile(wb, "failed_national_ids.xlsx")

      dispatch(showAlert({ text: t("Download successful!"), type: "success" }))
    } catch (error) {
      dispatch(
        showAlert({
          text: t("Download failed. Please try again."),
          type: "error",
        })
      )
    } finally {
      setTimeout(() => setIsDownloading(false), 1000) // Re-enable button after download
    }
  }

  return (
    <div>
      <div className="border rounded p-3">
        <div className="row mb-3">
          <div className="col-lg-6">
            <label htmlFor="uploadType">{t("Upload Type")}</label>
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="uploadType"
                  value="MANUAL"
                  checked={uploadType === "MANUAL"}
                  onClick={() => setUploadType("MANUAL")}
                  style={{ height: "unset" }}
                  className="px-2"
                />
                <label className="px-1 mb-0">{t("Manual")}</label>
              </div>
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="uploadType"
                  value="XLSX"
                  checked={uploadType === "XLSX"}
                  onClick={() => setUploadType("XLSX")}
                  style={{ height: "unset" }}
                  className="px-2"
                />
                <label className="px-1 mb-0">{t("Upload XLSX")}</label>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <label htmlFor="actionType">{t("Status")}</label>
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="actionType"
                  value="invited"
                  checked={actionType === "invited"}
                  onClick={() => setActionType("invited")}
                  style={{ height: "unset" }}
                  className="px-2"
                />
                <label className="px-1 mb-0">{t("Invited")}</label>
              </div>
              <div className="d-flex align-items-center">
                <input
                  type="radio"
                  name="actionType"
                  value="accepted"
                  checked={actionType === "accepted"}
                  onClick={() => setActionType("accepted")}
                  style={{ height: "unset" }}
                  className="px-2"
                />
                <label className="px-1 mb-0">{t("Accepted")}</label>
              </div>
            </div>
          </div>
        </div>
        {actionType === "invited" && (
          <textarea
            className="form-control mb-3"
            value={notification_message}
            onChange={
              e => setNotification_message(e.target.value.slice(0, 255)) // Limit 255 chars
            }
            placeholder={t("Notification Message")}
            maxLength={2000}
          />
        )}

        {uploadType === "MANUAL" ? (
          <div>
            <div className="form_field d-flex gap-2 my-3">
              <textarea
                name="national_ids"
                className="form-control"
                placeholder={t(
                  "Please enter national Id/ Iqama Id: eg. 1234657890, 2345678910"
                )}
                value={national_ids}
                onChange={e => {
                  const input = e.target.value
                  // Allow only numbers and commas, remove invalid characters
                  const filteredInput = input.replace(/[^0-9,]/g, "")
                  setNational_ids(filteredInput)
                }}
              ></textarea>
            </div>
            <div className="g_end mt-2 gap-3 cis_btn">
              <button
                className="main_btn disabled-button fit_content px-4"
                type="button"
                onClick={toggle}
                disabled={isLoading}
              >
                {t("Cancel")}
              </button>
              <button
                className="main_btn fit_content px-4"
                type="submit"
                disabled={isLoading}
                onClick={handleAdd}
              >
                {t("Add")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="form_field">
              <input
                type="file"
                accept=".xlsx"
                className="w-100"
                style={{ padding: 5 }}
                onChange={handleFileChange}
                ref={fileInputRef}
              />
              <div className="g_start my-3">
                <Download size={18} className="text-primary" />
                <a
                  href={InterviewCandidatesDemo}
                  download="Interview Candidates.xlsx"
                  className="px-1"
                >
                  {t("Download a demo XLSX file")}
                </a>
              </div>
            </div>
            {/* <button>{t("Download Template")}</button> */}
            <div className="g_end mt-2 gap-3 cis_btn">
              <button
                className="main_btn disabled-button fit_content px-4"
                type="button"
                onClick={toggle}
                disabled={isLoading}
              >
                {t("Cancel")}
              </button>
              <button
                className="main_btn fit_content px-4"
                type="submit"
                disabled={isLoading}
                onClick={handleCsvAdd}
              >
                {t("Upload")}
              </button>
            </div>
          </div>
        )}
        {isApiCalled && (
          <div className="row mt-5">
            <div className="col-lg-6">
              <div className="g_center text-success ">
                <h4>{t("Success")} : </h4>
                <h4>{saved_count ?? 0}</h4>
              </div>
            </div>
            {failed_national_ids?.length > 0 && (
              <div className="col-lg-6">
                <div className="g_center text-danger">
                  <h4>{t("Failed")} : </h4>
                  <h4>{failed_national_ids.length}</h4>
                </div>
                <button
                  className="btn btn-primary d_center"
                  onClick={downloadFailedIDs}
                  disabled={
                    isDownloading ||
                    !failed_national_ids ||
                    failed_national_ids.length === 0
                  }
                >
                  {isDownloading
                    ? t("Downloading...")
                    : t("Download Failed IDs")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AddCandidateInPool
